import { useEffect, useRef } from 'react';
import { ArrowRight, Mic } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import VoiceMicButton from '@/components/VoiceMicButton';

const MIN_HEIGHT = 72;
const MAX_HEIGHT = 160;

/**
 * v3 conversational layout — the pinned footer of `AssistantColumn`
 * (docs/design/consultation/Conversation.dc.html marker 7).
 *
 * "Record conversation" gets its own row above the message box, as a real
 * button rather than a small chip squeezed next to "Enter to send" — that
 * placement read as secondary/disabled and was easy to miss. It's the
 * doctor's other way of getting a note started (record the whole
 * consultation aloud vs. type/dictate one message at a time), so it earns
 * equal visual weight to the composer itself, not a footnote beside it.
 *
 * The message box grows with what's typed (up to MAX_HEIGHT, then scrolls)
 * instead of staying a fixed ~2 lines — typing a real clinical sentence
 * shouldn't immediately need vertical scrolling inside a 52px box.
 */
export function ConsultationComposer({
  value, onChange, onSend, onDictate, loading, placeholder, canRecord, recording, onToggleRecording,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** VoiceMicButton's transcribed text — appended at the current cursor/end. */
  onDictate: (text: string) => void;
  loading: boolean;
  placeholder: string;
  canRecord: boolean;
  recording: boolean;
  onToggleRecording: () => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = `${MIN_HEIGHT}px`;
    el.style.height = `${Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, el.scrollHeight))}px`;
  }, [value]);

  return (
    <div className="flex shrink-0 flex-col gap-1.5 border-t border-line px-2.5 py-2">
      {canRecord && (
        <Button type="button" size="sm" variant={recording ? 'primary' : 'ghost'} className="self-stretch justify-center" onClick={onToggleRecording}>
          <Mic className="h-3.5 w-3.5" aria-hidden="true" />
          {recording ? 'Stop recording' : 'Record conversation'}
        </Button>
      )}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          className="min-h-[72px] max-h-[160px] flex-1 resize-y rounded-[var(--radius)] border border-border bg-surface px-2.5 py-2 text-[12.5px] leading-relaxed text-ink outline-none placeholder:text-ink-muted focus:ring-2 focus:ring-teal/25"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
          }}
        />
        <div className="flex flex-col items-center gap-1.5 self-end pb-0.5">
          <VoiceMicButton onText={onDictate} title="Dictate your findings" />
          <Button type="button" size="icon" onClick={onSend} disabled={loading} aria-label="Send message">
            {loading ? '…' : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </Button>
        </div>
      </div>
      <span className="text-[11px] text-ink-muted">Enter to send · Shift+Enter for new line · drag the corner to resize</span>
    </div>
  );
}
